import {
    Box,
    Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';

import FormLinkButton from '../../components/buttons/FormLinkButton';

export interface PaymentAccountOption {
    id: string;
    label: string;
    subtitle?: string;
    isLastUsedAccount?: boolean;
    isUsedAccount?: boolean;
    isNotUsedAccount?: boolean;
    isFailedAccount?: boolean;
}

export const renderPaymentAccountSelectedValue = (
    option: PaymentAccountOption,
) => {
    return (
        <Box
            display='flex'
            alignItems='center'
            gap='8px'
            width='100%'
            minWidth={0}
            pr='28px'
        >
            <Typography
                component='span'
                fontSize='14px'
                fontWeight={500}
                noWrap
                sx={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {option.label}
            </Typography>

            {option.isLastUsedAccount && (
                <Typography
                    component='span'
                    fontSize='12px'
                    color='text.disabled'
                    flexShrink={0}
                >
                    (last used)
                </Typography>
            )}

            {(option.isLastUsedAccount || option.isUsedAccount) && (
                <DoneAllIcon
                    sx={{
                        fontSize: '18px',
                        color: 'success.main',
                        flexShrink: 0,
                    }}
                />
            )}

            {option.isNotUsedAccount && (
                <CheckIcon
                    sx={{
                        fontSize: '18px',
                        color: 'warning.main',
                        flexShrink: 0,
                    }}
                />
            )}

            {option.isFailedAccount && (
                <CheckIcon
                    sx={{
                        fontSize: '18px',
                        color: 'error.main',
                        flexShrink: 0,
                    }}
                />
            )}
        </Box>
    );
};

export const renderPaymentAccountOption = (
    option: PaymentAccountOption,
    selected: boolean,
) => {
    return (
        <Box
            display='flex'
            alignItems='center'
            width='100%'
            gap='10px'
        >
            <Box
                width='18px'
                display='flex'
                alignItems='center'
                justifyContent='center'
                flexShrink={0}
            >
                {selected && (
                    <CheckIcon
                        sx={{
                            fontSize: '18px',
                        }}
                    />
                )}
            </Box>

            <Typography
                component='span'
                fontSize='14px'
                fontWeight={500}
            >
                {option.label}
            </Typography>

            {option.isLastUsedAccount && (
                <Typography
                    component='span'
                    fontSize='12px'
                    color='text.disabled'
                >
                    (last used)
                </Typography>
            )}

            {(option.isLastUsedAccount || option.isUsedAccount) && (
                <DoneAllIcon
                    sx={{
                        fontSize: '18px',
                        color: 'success.main',
                    }}
                />
            )}

            {option.isNotUsedAccount && (
                <CheckIcon
                    sx={{
                        fontSize: '18px',
                        color: 'warning.main',
                    }}
                />
            )}

            {option.isFailedAccount && (
                <CheckIcon
                    sx={{
                        fontSize: '18px',
                        color: 'error.main',
                    }}
                />
            )}
        </Box>
    );
};

export const renderPaymentAccountInfo = (
    selectedPaymentAccount: PaymentAccountOption | undefined,
    vendorAccountId?: string | number | null,
) => {
    if (!selectedPaymentAccount) {
        return null;
    }

    return (
        <Box
            mt='8px'
            display='flex'
            alignItems='center'
            gap='8px'
            width='100%'
        >
            <Box
                display='flex'
                alignItems='center'
                gap='6px'
                marginTop='6px'
            >
                {selectedPaymentAccount.isFailedAccount ? (
                    <>
                        <CheckIcon
                            sx={{
                                fontSize: '16px',
                                color: 'error.main',
                                flexShrink: 0,
                            }}
                        />

                        <Typography
                            fontSize='12px'
                            color='text.secondary'
                        >
                            This account was used in a payment attempt that failed.
                        </Typography>
                    </>
                ) : selectedPaymentAccount.isNotUsedAccount ? (
                    <>
                        <CheckIcon
                            sx={{
                                fontSize: '16px',
                                color: 'warning.main',
                                flexShrink: 0,
                            }}
                        />

                        <Typography
                            fontSize='12px'
                            color='text.secondary'
                        >
                            This account has not been used for any payment yet.
                        </Typography>
                    </>
                ) : selectedPaymentAccount.isLastUsedAccount ? (
                    <>
                        <DoneAllIcon
                            sx={{
                                fontSize: '16px',
                                color: 'success.main',
                                flexShrink: 0,
                            }}
                        />

                        <Typography
                            fontSize='12px'
                            color='text.secondary'
                        >
                            This is the account used for the vendor&apos;s most recent payment.
                        </Typography>
                    </>
                ) : selectedPaymentAccount.isUsedAccount ? (
                    <>
                        <DoneAllIcon
                            sx={{
                                fontSize: '16px',
                                color: 'success.main',
                                flexShrink: 0,
                            }}
                        />

                        <Typography
                            fontSize='12px'
                            color='text.secondary'
                        >
                            This account has been used for a prior payment, but it was not the most recent one.
                        </Typography>
                    </>
                ) : null}
            </Box>

            {vendorAccountId && (
                <Box
                    display='flex'
                    alignItems='center'
                    gap='4px'
                    flexShrink={0}
                >
                    <FormLinkButton
                        path={`/vendors/edit-vendor-account/${vendorAccountId}`}
                        name='View Vendor'
                    />
                </Box>
            )}
        </Box>
    );
};
